import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import ItemizedLineItemsEditor, { type ItemizedLineItem } from "./ItemizedLineItemsEditor";

type OwnerOption = { id: number; fullName?: string | null; username?: string | null; isActive?: boolean | null; role?: string | null };
type OrgOption = { id: number; name: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  owners: OwnerOption[];
  billedToOrgOptions: OrgOption[];
};

function invalidateInvoiceQueries() {
  queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
  queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
  queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
}

export default function GenerateInvoiceDialog({ open, onOpenChange, owners, billedToOrgOptions }: Props) {
  const { toast } = useToast();
  const [invoiceKind, setInvoiceKind] = useState<"reconciliation" | "itemized">("reconciliation");
  const [invoiceMode, setInvoiceMode] = useState<"owner" | "org">("owner");
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("");
  const [selectedBilledToOrgId, setSelectedBilledToOrgId] = useState<string>("");
  const [periodStart, setPeriodStart] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0] ?? "";
  });
  const [periodEnd, setPeriodEnd] = useState<string>(() => new Date().toISOString().split("T")[0] ?? "");
  const [itemizedInvoiceDate, setItemizedInvoiceDate] = useState<string>(() => new Date().toISOString().split("T")[0] ?? "");
  const [itemizedLineItems, setItemizedLineItems] = useState<ItemizedLineItem[]>([
    { description: "", category: "supplies", amount: "", date: "" },
  ]);
  const [invoiceNotes, setInvoiceNotes] = useState("");

  const resetForm = () => {
    setInvoiceNotes("");
    setSelectedOwnerId("");
    setSelectedBilledToOrgId("");
    setInvoiceMode("owner");
    setInvoiceKind("reconciliation");
    setItemizedLineItems([{ description: "", category: "supplies", amount: "", date: "" }]);
  };

  const createInvoiceMutation = useMutation({
    mutationFn: async (data: { ownerUserId?: number; billedToOrgId?: number; periodStart: string; periodEnd: string; notes?: string }) => {
      const res = await apiRequest("POST", "/api/admin/owner-invoices", data);
      return res.json();
    },
    onSuccess: () => {
      invalidateInvoiceQueries();
      onOpenChange(false);
      resetForm();
      toast({ title: "Invoice created", description: "Draft invoice has been generated." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message || "Failed to create invoice", variant: "destructive" });
    },
  });

  const createItemizedInvoiceMutation = useMutation({
    mutationFn: async (data: {
      ownerUserId?: number;
      billedToOrgId?: number;
      invoiceDate: string;
      lineItems: Array<{ description: string; category: string; amount: string; date?: string }>;
      notes?: string;
    }) => {
      const res = await apiRequest("POST", "/api/admin/owner-invoices/itemized", data);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || "Failed to create itemized invoice");
      }
      return res.json();
    },
    onSuccess: () => {
      invalidateInvoiceQueries();
      onOpenChange(false);
      resetForm();
      toast({ title: "Invoice created", description: "Itemized draft invoice has been generated." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const targetMissing = invoiceMode === "owner" ? !selectedOwnerId : !selectedBilledToOrgId;
  const isPending = createInvoiceMutation.isPending || createItemizedInvoiceMutation.isPending;

  const validItemizedItems = itemizedLineItems.filter(it =>
    it.description.trim() && it.amount.trim() && !isNaN(Number(it.amount)) && Number(it.amount) !== 0
  );

  const submitDisabled = targetMissing || isPending || (
    invoiceKind === "reconciliation"
      ? (!periodStart || !periodEnd)
      : (!itemizedInvoiceDate || validItemizedItems.length === 0)
  );

  const onSubmit = () => {
    if (invoiceKind === "reconciliation") {
      const base = { periodStart, periodEnd, notes: invoiceNotes || undefined };
      createInvoiceMutation.mutate(invoiceMode === "org"
        ? { ...base, billedToOrgId: Number(selectedBilledToOrgId) }
        : { ...base, ownerUserId: Number(selectedOwnerId) });
      return;
    }
    const cleanItems = validItemizedItems.map(it => ({
      description: it.description.trim(),
      category: it.category,
      amount: String(Number(it.amount)),
      ...(it.date ? { date: it.date } : {}),
    }));
    const payload = {
      invoiceDate: itemizedInvoiceDate,
      lineItems: cleanItems,
      notes: invoiceNotes || undefined,
      ...(invoiceMode === "org"
        ? { billedToOrgId: Number(selectedBilledToOrgId) }
        : { ownerUserId: Number(selectedOwnerId) }),
    };
    createItemizedInvoiceMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Invoice</DialogTitle>
          <DialogDescription>
            {invoiceKind === "reconciliation"
              ? "Auto-generate line items from financial records for a period."
              : "Manually add line items for one-off expenses, supplies, or unusual work."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex rounded-md border overflow-hidden text-sm">
            <button
              type="button"
              className={`flex-1 px-3 py-2 transition-colors ${invoiceKind === "reconciliation" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              onClick={() => setInvoiceKind("reconciliation")}
              data-testid="tab-reconciliation"
            >From Reconciliation</button>
            <button
              type="button"
              className={`flex-1 px-3 py-2 transition-colors ${invoiceKind === "itemized" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              onClick={() => setInvoiceKind("itemized")}
              data-testid="tab-itemized"
            >Itemized (Custom)</button>
          </div>

          {billedToOrgOptions.length > 0 && (
            <div className="flex rounded-md border overflow-hidden text-sm">
              <button
                type="button"
                className={`flex-1 px-3 py-2 transition-colors ${invoiceMode === "owner" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                onClick={() => setInvoiceMode("owner")}
              >Bill Property Manager</button>
              <button
                type="button"
                className={`flex-1 px-3 py-2 transition-colors ${invoiceMode === "org" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                onClick={() => setInvoiceMode("org")}
              >Bill PM Organization</button>
            </div>
          )}

          {invoiceMode === "owner" ? (
            <div>
              <Label>Property Manager</Label>
              <SearchableSelect
                inDialog
                value={selectedOwnerId}
                onValueChange={setSelectedOwnerId}
                options={owners.filter(o => o.isActive !== false).map(o => ({ value: String(o.id), label: o.fullName || o.username || `User ${o.id}` }))}
                placeholder="Select a property manager"
                data-testid="select-owner"
                className="w-full"
              />
            </div>
          ) : (
            <div>
              <Label>PM Organization</Label>
              <SearchableSelect
                inDialog
                value={selectedBilledToOrgId}
                onValueChange={setSelectedBilledToOrgId}
                options={billedToOrgOptions.map(o => ({ value: String(o.id), label: o.name }))}
                placeholder="Select the PM org to bill"
                className="w-full"
              />
              {invoiceKind === "reconciliation" && (
                <p className="text-xs text-muted-foreground mt-1">Line items will be auto-generated from your financial records for this org&apos;s apartments in the period.</p>
              )}
            </div>
          )}

          {invoiceKind === "reconciliation" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} data-testid="input-period-start" />
              </div>
              <div>
                <Label>Period End</Label>
                <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} data-testid="input-period-end" />
              </div>
            </div>
          ) : (
            <ItemizedLineItemsEditor
              invoiceDate={itemizedInvoiceDate}
              onInvoiceDateChange={setItemizedInvoiceDate}
              items={itemizedLineItems}
              onItemsChange={setItemizedLineItems}
            />
          )}

          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)} className="resize-none" data-testid="input-notes" />
          </div>

          <Button className="w-full" disabled={submitDisabled} onClick={onSubmit} data-testid="button-submit-invoice">
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Generate Invoice
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
